import type { AppManagerType } from "@/lib/application/AppManager";
import type {
	AppStoreProps,
	ModalIds,
} from "@/lib/application/stores/appStore";
import {
	type BetterAuthSession,
	useBetterAuthStore,
} from "@/lib/application/stores/betterAuthStore";
import { UserAnalytics } from "@/lib/userAnalytics";
import { usePostHog } from "posthog-js/react";
import { createContext, useContext } from "react";

interface AppContextProps {
	appStore: AppStoreProps;
	appManager: AppManagerType;
	audioManager: AppManagerType["audioManager"];
	authManager: AppManagerType["authManager"];
	betterAuthManager: AppManagerType["betterAuthManager"];
	betterAuthSession: BetterAuthSession | null;
	featureFlags: Record<string, unknown>;
	favoriteTrackRepository: AppManagerType["favoriteTrackRepository"];
	isSubscribed: boolean;
	openedModalId: ModalIds;
	paymentManager: AppManagerType["paymentManager"];
	resetSignupTimer: AppManagerType["resetSignupTimer"];
	userAnalytics: UserAnalytics | null;
	handleModalOpen: (modalId: ModalIds, isVisible: boolean) => void;
}

const AppContext = createContext<AppContextProps | undefined>(undefined);

export const useApp = () => {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error("useApp must be used within an AppProvider");
	}
	return context;
};

const AppProvider: React.FC<{
	appManager: AppManagerType;
	children: React.ReactNode;
}> = ({ appManager, children }) => {
	const { openedModalId, isUserSubscribed } = appManager.useStore();
	const appStore = appManager.useStore();
	const {
		audioManager,
		authManager,
		betterAuthManager,
		favoriteTrackRepository,
		paymentManager,
		resetSignupTimer,
	} = appManager;
	const { session: betterAuthSession } = useBetterAuthStore();
	const postHog = usePostHog();

	// setup our feature flag variant keys
	const featureFlags = appManager.featureFlags;

	let userAnalytics: UserAnalytics | null = null;
	if (postHog) {
		// Initialize UserAnalytics with PostHog if available
		userAnalytics = UserAnalytics.getInstance(postHog);
	}

	const handleModalOpen = (modalId: ModalIds, isVisible: boolean) => {
		appManager.showModal(modalId, isVisible);
	};

	return (
		<AppContext.Provider
			value={{
				appStore,
				appManager,
				audioManager,
				authManager,
				betterAuthManager,
				betterAuthSession,
				featureFlags,
				favoriteTrackRepository,
				isSubscribed: isUserSubscribed,
				openedModalId,
				handleModalOpen,
				paymentManager,
				resetSignupTimer,
				userAnalytics,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

export default AppProvider;
